// ============================================================================
// CHANGO V5.0 — <ReconsentGate> (§11.10 frontend enforcement)
// ============================================================================
// Mounted once inside <AppShell /> (i.e. behind RequireAuth). Registers itself
// as the global re-consent handler with api/client.js. Whenever any write
// request comes back with 409 AGREEMENT_VERSION_MISMATCH, the client calls the
// registered handler with the 409 details; this component:
//
//   1. Fetches the current (is_current=1) TOS + Privacy agreements so it can
//      show the exact text the workspace must re-accept (markdown → DOMPurify).
//   2. Renders a blocking modal (no close / no escape until re-consent done).
//      Only the stale types are required — if the 409 only flags 'tos', the
//      user sees a single-tab modal.
//   3. On "我已阅读并同意":
//        for each stale agreement type → POST /api/legal/consent with
//          { agreement_id, consent_scope: 'reprompt_version_change' }
//        The server then patches workspace.agreed_{tos,privacy}_version so
//        subsequent writes pass the middleware.
//   4. Resolves the handler-promise, which triggers the client to transparently
//      retry the original request.
//
// If the user clicks "退出账号" the handler is rejected → the client keeps the
// 409 intact and the caller sees its own error UI. We also force-logout for
// hygiene: once a tenant is blocked, there is nothing else they can do.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

import { api, setReconsentHandler, HttpError } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

// Parse the middleware's `mismatch` string ("tos" | "privacy" | "tos,privacy")
// into a Set of stale types. Forgiving: unknown tokens are ignored.
function parseMismatch(raw) {
  if (!raw || typeof raw !== 'string') return new Set();
  return new Set(
    raw.split(',')
       .map((s) => s.trim())
       .filter((s) => s === 'tos' || s === 'privacy')
  );
}

function renderMarkdown(md) {
  if (!md) return '';
  const html = marked.parse(md, { breaks: true, gfm: true });
  return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
}

const TYPE_LABEL = {
  tos: '用户服务协议',
  privacy: '隐私政策',
};

export default function ReconsentGate() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // A single outstanding "call" slot — the handler is serialised upstream by
  // _reconsentInFlight, so only one is ever active, but we still store it as
  // state so re-renders carry the resolvers.
  const [call, setCall] = useState(null); // { details, resolve, reject }
  const [agreements, setAgreements] = useState(null); // { tos, privacy }
  const [loading, setLoading] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Which tabs are open / have been scrolled through.
  const staleTypes = useMemo(() => parseMismatch(call?.details?.mismatch), [call]);
  const [viewed, setViewed] = useState({ tos: false, privacy: false });
  const [tab, setTab] = useState('tos');
  const bodyRef = useRef(null);

  // ---- handler registration ------------------------------------------------
  useEffect(() => {
    const handler = (details) => new Promise((resolve, reject) => {
      setCall({ details: details || {}, resolve, reject });
    });
    setReconsentHandler(handler);
    return () => setReconsentHandler(null);
  }, []);

  // When a new call comes in, switch to the first stale tab and reset scroll tracking.
  useEffect(() => {
    if (!call) return;
    const firstStale = staleTypes.has('tos') ? 'tos'
                     : staleTypes.has('privacy') ? 'privacy'
                     : 'tos';
    setTab(firstStale);
    setViewed({ tos: false, privacy: false });
    setSubmitErr(null);
  }, [call, staleTypes]);

  // ---- fetch current agreements --------------------------------------------
  useEffect(() => {
    if (!call) { setAgreements(null); return; }
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get('/api/legal/agreements?is_current=1');
        const list = Array.isArray(res?.agreements) ? res.agreements : [];
        if (!alive) return;
        setAgreements({
          tos: list.find((a) => a.agreement_type === 'tos') || null,
          privacy: list.find((a) => a.agreement_type === 'privacy') || null,
        });
      } catch (err) {
        if (!alive) return;
        setAgreements({ tos: null, privacy: null });
        setSubmitErr(err instanceof HttpError ? err.message : '加载协议失败');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [call]);

  // ---- scroll tracking (soft guard) ----------------------------------------
  const onScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight + 4 >= el.scrollHeight;
    if (!atBottom) return;
    if (tab === 'tos') setViewed((v) => ({ ...v, tos: true }));
    if (tab === 'privacy') setViewed((v) => ({ ...v, privacy: true }));
  }, [tab]);

  // ---- confirmability ------------------------------------------------------
  // Only stale types need to be viewed. If the modal received a stale 'tos'
  // but 'privacy' is already fresh, we don't require scrolling privacy.
  const readyToSubmit = useMemo(() => {
    if (!agreements) return false;
    for (const t of staleTypes) {
      const row = agreements[t];
      if (!row?.id) return false;
      if (!viewed[t]) return false;
    }
    return staleTypes.size > 0;
  }, [agreements, viewed, staleTypes]);

  // ---- submit: POST consent per stale type ---------------------------------
  const submit = async () => {
    if (!readyToSubmit || submitting) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      for (const t of staleTypes) {
        const row = agreements[t];
        // eslint-disable-next-line no-await-in-loop
        await api.post('/api/legal/consent', {
          agreement_id: row.id,
          consent_scope: 'reprompt_version_change',
        });
      }
      // Done — close modal + resolve upstream to trigger original-write retry.
      const { resolve } = call;
      setCall(null);
      setAgreements(null);
      resolve();
    } catch (err) {
      setSubmitErr(err instanceof HttpError ? err.message : '提交失败，请稍后再试');
      setSubmitting(false);
    } finally {
      // If resolved-success path took effect, submitting stays visually true
      // because we cleared the modal anyway.
      if (call) setSubmitting(false);
    }
  };

  const onForceLogout = async () => {
    // Reject the handler so the caller sees its original 409, then log out.
    const rejectFn = call?.reject;
    setCall(null);
    setAgreements(null);
    if (rejectFn) rejectFn(new Error('user cancelled reconsent'));
    try { await logout(); } catch { /* best-effort */ }
    navigate('/login', { replace: true });
  };

  // ---- render --------------------------------------------------------------
  if (!call) return null;

  const currentHtml = (() => {
    if (!agreements) return '';
    const row = tab === 'tos' ? agreements.tos : agreements.privacy;
    return renderMarkdown(row?.body_markdown);
  })();

  const visibleTabs = ['tos', 'privacy'].filter((t) => staleTypes.has(t));
  const tabsToRender = visibleTabs.length > 0 ? visibleTabs : ['tos', 'privacy'];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconsent-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4"
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
        <header className="border-b border-slate-200 px-6 py-4">
          <h2 id="reconsent-title" className="text-base font-semibold text-slate-800">
            请重新确认协议条款
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            由于 {Array.from(staleTypes).map((t) => TYPE_LABEL[t]).join(' / ') || '协议'} 已发布新版本，
            为保证合规，完成任何变更操作前需要您重新阅读并同意。期间只读视图不受影响。
          </p>
        </header>

        <nav className="flex gap-2 border-b border-slate-200 px-4 pt-3" role="tablist">
          {tabsToRender.map((t) => {
            const row = agreements?.[t];
            const stale = staleTypes.has(t);
            const active = tab === t;
            return (
              <button
                key={t}
                role="tab"
                aria-selected={active}
                type="button"
                onClick={() => setTab(t)}
                className={[
                  'px-3 py-2 text-sm font-medium transition-colors',
                  active
                    ? 'border-b-2 border-chango-500 text-chango-700'
                    : 'text-slate-500 hover:text-slate-700',
                ].join(' ')}
              >
                {TYPE_LABEL[t]}
                {row?.version && (
                  <span className="ml-2 text-xs text-slate-400">({row.version})</span>
                )}
                {stale && (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">
                    需要重新同意
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div
          ref={bodyRef}
          onScroll={onScroll}
          className="prose prose-slate max-w-none flex-1 overflow-auto px-6 py-4 text-sm leading-6"
        >
          {loading ? (
            <p className="text-slate-500">加载中…</p>
          ) : !agreements ? (
            <p className="text-slate-500">无法加载当前协议，请稍后重试。</p>
          ) : currentHtml ? (
            <div dangerouslySetInnerHTML={{ __html: currentHtml }} />
          ) : (
            <p className="text-slate-500">
              当前 {TYPE_LABEL[tab]} 无可展示内容；请联系管理员修复 is_current=1 行。
            </p>
          )}
        </div>

        {submitErr && (
          <div className="border-t border-rose-200 bg-rose-50 px-6 py-2 text-sm text-rose-800">
            {submitErr}
          </div>
        )}

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-3">
          <p className="text-xs text-slate-500">
            {readyToSubmit
              ? '✓ 已阅读所需全部文档，可以提交。'
              : staleTypes.size === 0
                ? '（无需重新同意）'
                : `请滚动至 ${
                    Array.from(staleTypes)
                      .filter((t) => !viewed[t])
                      .map((t) => TYPE_LABEL[t])
                      .join(' / ')
                  } 底部以启用按钮`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onForceLogout}
              disabled={submitting}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50"
            >
              退出账号
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!readyToSubmit || submitting}
              className="rounded-md bg-chango-600 px-4 py-2 text-sm font-semibold text-white hover:bg-chango-700 disabled:opacity-50"
            >
              {submitting ? '提交中…' : '我已阅读并同意'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
